interface DeStringModule {
  isReady?: boolean;
  _DeString?: (ptr: number) => number;
  _FreeMemory?: (ptr: number) => void;
  _free?: (ptr: number) => void;
}

interface DecryptRuntimeGlobals {
  Module?: DeStringModule;
  allocateUTF8?: (text: string) => number;
  UTF8ToString?: (ptr: number) => string;
}

let deStringLoadPromise: Promise<void> | null = null;

/**
 * 解析 deString.js 的加载地址（阅读端 ./deString，开发态由 public/deString 提供）。
 */
const getDeStringScriptUrl = (): string => {
  const existing = document.querySelector<HTMLScriptElement>('script[src*="deString"]');
  if (existing?.src) return existing.src;

  const base = import.meta.env.BASE_URL ?? '/';
  if (import.meta.env.DEV && base === '/') {
    return '/deString/deString.js';
  }

  return `${base}deString/deString.js`;
};

/**
 * 确保 deString wasm 运行时已加载并完成初始化。
 */
const ensureDeStringRuntime = (): Promise<void> => {
  if (deStringLoadPromise) return deStringLoadPromise;

  deStringLoadPromise = new Promise((resolve, reject) => {
    const runtime = globalThis as typeof globalThis & DecryptRuntimeGlobals;

    const waitUntilReady = () => {
      if (
        runtime.Module?.isReady &&
        runtime.Module._DeString &&
        runtime.Module._FreeMemory &&
        runtime.Module._free &&
        runtime.allocateUTF8 &&
        runtime.UTF8ToString
      ) {
        resolve();
        return;
      }

      setTimeout(waitUntilReady, 50);
    };

    // 步骤 1：若 HTML 已引入脚本，仅等待 wasm 初始化完成。
    if (document.querySelector('script[src*="deString"]')) {
      waitUntilReady();
      return;
    }

    // 步骤 2：未引入时动态加载 deString.js，再等待初始化。
    const script = document.createElement('script');
    script.src = getDeStringScriptUrl();
    script.async = false;
    script.onload = () => waitUntilReady();
    script.onerror = () => reject(new Error(`无法加载解密脚本：${script.src}`));
    document.head.appendChild(script);
  });

  return deStringLoadPromise;
};

/**
 * 解密项目配置字符串（等待 wasm Runtime 初始化后再执行）。
 */
export const decryptProjectConfig = async (encryptedText: string): Promise<string> => {
  await ensureDeStringRuntime();

  const runtime = globalThis as typeof globalThis & DecryptRuntimeGlobals;
  const moduleRef = runtime.Module!;
  const allocateUTF8Ref = runtime.allocateUTF8!;
  const utf8ToStringRef = runtime.UTF8ToString!;

  try {
    const ptr = allocateUTF8Ref(encryptedText);
    const out = moduleRef._DeString!(ptr);
    let pagesValue = utf8ToStringRef(out).replaceAll('\r\n', '');
    moduleRef._FreeMemory!(out);

    const lastBraceIndex = pagesValue.lastIndexOf('}');
    pagesValue = pagesValue.substring(0, lastBraceIndex + 1);

    moduleRef._free!(ptr);
    return pagesValue;
  } catch (error) {
    console.error('解密失败:', error);
    throw error;
  }
};


/* 解码 base64 字符串 */
export const decodeBase64 = (base64: string): string => {
  return atob(base64);
};